import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { BoxEnvelope } from '@lib/models/box-enveloppe.model';
import { OperationSpecificData } from '@lib/models/donees-specifiques.model';

import { ENVELOPPE_TO_MERGE_LIST, FAKE_ENVELOPPE_MERGED } from '@lib/models/box-additionnal-data';
import { AuthService } from '@lib/services/auth-service.service';
import { OptionsService } from '@lib/services/options.service';
import { ProduitService } from '@lib/services/produit.service';
import { OuiBannerAction } from 'omnium-ui/banner';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiAutocompleteOption } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import {
  Operation,
  BackOfficeService,
  OperationConfig,
  OperationType,
  Produit,
  TransactionPersonnelleEnum,
} from 'projects/box-lib/src/public-api';
import { NoneActiveHabilitationMessageComponent } from 'src/app/components/none-active-habilitation-message/none-active-habilitation-message.component';
import { DraftOperationsService } from 'src/service/draft-operation/draft-operation.service';
import { DeleteProduitDialogComponent } from '../delete-produit-dialog/delete-produit-dialog.component';
import { OperationSpecificDataValue } from '../operation-specific/abstract-specific-data.component';
import { EnveloppeChangeConfirmationDialogComponent } from './enveloppe-change-confirmation-dialog/enveloppe-change-confirmation-dialog.component';
import { OperationInformationFormComponent } from '../operation-information-form/operation-information-form.component';

@Component({
    selector: 'app-step-informations',
    templateUrl: './step-informations.component.html',
    styleUrls: ['./step-informations.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StepInformationsComponent implements OnInit, AfterViewInit {
  @Output() specificDataChange = new EventEmitter<{ value: OperationSpecificDataValue; index: number }>();
  @Output() operationConfigChange = new EventEmitter<{ value: OperationConfig | null; index: number }>();
  @Output() productChange = new EventEmitter<{ value: Produit | null; index: number }>();
  @Output() addOperationClicked = new EventEmitter();
  @Output() deleteClicked = new EventEmitter<Operation>();

  @ViewChildren(OperationInformationFormComponent)
  operationForms!: QueryList<OperationInformationFormComponent>;
  addButtonLabel: string;

  enveloppeControl = new FormControl<number | null>(null, Validators.required);
  enveloppeAutocompleteOptions: OuiAutocompleteOption<number>[] = [];
  selectedEnveloppeId?: number;
  isSelectedEnvelopeHabilited = false;
  isSouscription = false;

  protected operationTypeEnum = OperationType;

  private produitsActifs: Produit[] = [];

  selectableProduits: Produit[] = [];

  selectedEnveloppeHabilitationIds: Set<number>;

  get isPsiTransaction(): boolean {
    return this.selectableProduits.length > 0 && this.selectableProduits.every(
      produit => produit.enveloppeNavigation?.backOfficeService === BackOfficeService.Psi
    );
  }

  habilitedEnvelopeIds: Set<number> = new Set();

  private readonly ENVELOPPE_TO_MERGE_IDS = ENVELOPPE_TO_MERGE_LIST.map(e => e.code);

  constructor(
    protected draftOperationsService: DraftOperationsService,
    private dialogService: OuiDialogService,
    private optionsService: OptionsService,
    private snackbarService: OuiSnackbarService,
    private produitService: ProduitService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.addButtonLabel =
      this.draftOperationsService.operationGroupData.operationType === OperationType.ActeDeGestion
        ? 'Ajouter un acte de gestion'
        : 'Ajouter un produit';
  }

  ngOnInit(): void {
    this.isSouscription = this.draftOperationsService.operationGroupData.operationType === OperationType.Souscription;
    this.enveloppeControl.valueChanges.subscribe(enveloppeId => {
      if (!enveloppeId || enveloppeId === this.selectedEnveloppeId) {
        return;
      }
      if (!this.selectedEnveloppeId) {
        this.updateSelectedEnveloppe(enveloppeId);
        return;
      }

      //if we have already a product in the operation group, then we display the popup
      if (this.draftOperationsService.operationGroupData.operations.some(op => op.produit)) {
        const dialogRef = this.dialogService.openDialog(
          EnveloppeChangeConfirmationDialogComponent,
          undefined,
          'auto',
          '480px'
        );
        const dialogSub = dialogRef.componentInstance.confirm.subscribe(async () => {
          try {
            await this.draftOperationsService.resetDraftOperationGroup();
            // this.draftOperationsService.operationGroupData.operations.forEach(async op => {
            //   await this.draftOperationsService.deleteDraftOperation(op);
            // });
            // this.addOperationClicked.emit();
            this.updateSelectedEnveloppe(enveloppeId);
            dialogRef.close(true);
          } catch (err) {
            this.snackbarService.open("Une erreur est survenue lors du changement d'enveloppe", 'error');
          }
        });

        dialogRef.afterClosed().subscribe((res: boolean | undefined) => {
          // Pas de confirmation => on remet d'ancienne valeur de l'enveloppe
          if (!res) {
            this.enveloppeControl.setValue(this.selectedEnveloppeId!);
          }
          dialogSub.unsubscribe();
        });

        return;
      }

      this.updateSelectedEnveloppe(enveloppeId);
    });
  }

  ngAfterViewInit(): void {
    this.initEnveloppes();
  }

  /**
   * Update the selected enveloppe and its related properties (selectedEnveloppeId, selectableProduits, isSelectedEnvelopeHabilited)
   * @param enveloppeId the id of the new selected enveloppe
   */
  private updateSelectedEnveloppe(enveloppeId: number) {
    this.selectedEnveloppeId = enveloppeId;
    this.updateSelectableProducts();
    this.isSelectedEnvelopeHabilited = this.habilitedEnvelopeIds.has(enveloppeId);
  }

  /**
   * Initializes the list of available enveloppes and sets the autocomplete options for enveloppe selection.
   *
   * This function retrieves all active products and filters them to construct a unique list of enveloppes.
   * It also applies a business rule to merge certain enveloppes (FIP and FCPI) into a single enveloppe.
   * The function updates the set of habilitated enveloppe IDs based on user habilitations.
   * Finally, it sets the current enveloppe code in the form control.
   *
   * @private
   * @async
   */

  private async initEnveloppes() {
    this.produitsActifs = await this.produitService.getProduitsActifs();
    let enveloppes: BoxEnvelope[] = [];
    let idsEnveloppes = new Set();

    // Récupérer la liste des enveloppes disponibles
    this.produitsActifs.forEach(produit => {
      if (produit.enveloppeNavigation) {
        // Here we add a little trick to merge FIP and FCPI enveloppe as requested by business
        let isMerged = false;
        if (this.ENVELOPPE_TO_MERGE_IDS.includes(produit.enveloppeNavigation.code)) {
          isMerged = true;
        }
        let enveloppe: BoxEnvelope = isMerged ? FAKE_ENVELOPPE_MERGED : (produit.enveloppeNavigation! as BoxEnvelope);

        if (!idsEnveloppes.has(enveloppe.code)) {
          idsEnveloppes.add(enveloppe.code);
          enveloppes.push(enveloppe);
        }
        if (!this.habilitedEnvelopeIds.has(enveloppe.code) && this.authService.isUserHabilitedSouscription(produit)) {
          this.habilitedEnvelopeIds.add(enveloppe.code);
        }
      }
    });

    this.enveloppeAutocompleteOptions = this.optionsService.getEnveloppeAutocompleteOptions(enveloppes);
    const currentEnveloppeCode =
      this.draftOperationsService.operationGroupData.operations[0]?.produit?.enveloppeNavigation?.code ??
      this.draftOperationsService.operationGroupData.operations[0]?.operationConfig?.enveloppeId;
    if (currentEnveloppeCode) {
      setTimeout(() => {
        // Here we also check if the current enveloppe is in the list of enveloppe to merge
        const currentValue = this.ENVELOPPE_TO_MERGE_IDS.includes(currentEnveloppeCode)
          ? FAKE_ENVELOPPE_MERGED.code
          : currentEnveloppeCode;
        this.enveloppeControl.setValue(currentValue);
      });
    }
  }

  /**
   * Update the list of selectable produits according to the selected enveloppe
   *
   * If the selected enveloppe is one of the enveloppe to merge, it will return all the produits
   * that have the same enveloppe code as the selected enveloppe or the enveloppe to merge
   * This is the case for FIP and FCPI enveloppes merged into FIP/PFCPI enveloppe
   * @private
   * @memberof StepInformationsComponent
   */
  private updateSelectableProducts(): void {
    this.selectableProduits = this.produitsActifs.filter(p => {
      return (
        p.enveloppeNavigation?.code === this.selectedEnveloppeId ||
        (this.selectedEnveloppeId === FAKE_ENVELOPPE_MERGED.code &&
          this.ENVELOPPE_TO_MERGE_IDS.includes(p.enveloppeNavigation?.code))
      );
    });
    this.selectedEnveloppeHabilitationIds = new Set(this.selectableProduits.map(p => p.habilitationNavigation?.id));
  }
  updateTransactionPersonelleStatut(value: TransactionPersonnelleEnum | undefined) {
    this.draftOperationsService.setTransactionPersonnelleStatutValue(value);
  }
  onOperationConfigChanged(config: OperationConfig | null, index: number) {
    if (config?.id && config.id != this.draftOperationsService.operationGroupData.operations[index].operationConfigId) {
      this.draftOperationsService.operationGroupData.operations[index].donneesSpecifiques = '{}';
      this.draftOperationsService.operationGroupData.operations[index].specificDataValues = {
        isFormValid: false,
        data: {},
      };
    }
    this.draftOperationsService.operationGroupData.operations[index].operationConfig = config;
    this.draftOperationsService.operationGroupData.operations[index].operationConfigId = config?.id;
  }

  onSpecificDataChange(newData: OperationSpecificDataValue, index: number) {
    const operation = this.draftOperationsService.operationGroupData.operations[index];
    // Les données reçues de Proxeema ne sont pas saisies dans le formulaire : les conserver lors de sa réécriture.
    const proxeemaData = this.getProxeemaSpecificData(operation.donneesSpecifiques);
    newData = { ...newData, data: { ...proxeemaData, ...newData.data } };
    operation.specificDataValues = newData;
    operation.donneesSpecifiques = JSON.stringify(newData.data);
    // Mapping montant qui n'est plus une donnée spécifique
    operation.montant = newData.data.montant;
  }

  private getProxeemaSpecificData(donneesSpecifiques: string | null | undefined): Partial<OperationSpecificData> {
    if (!donneesSpecifiques) return {};
    try {
      const data: OperationSpecificData = JSON.parse(donneesSpecifiques);
      return data.gammesFondsEvenementiels ? { gammesFondsEvenementiels: data.gammesFondsEvenementiels } : {};
    } catch {
      return {};
    }
  }

  onProductChanged(produit: Produit | null, index: number) {
    this.draftOperationsService.operationGroupData.operations[index].produit = produit;
    this.draftOperationsService.operationGroupData.operations[index].produitId = produit?.id;
  }

  deleteOperation(operation: Operation): void {
    this.dialogService.openDialog(DeleteProduitDialogComponent, { operation: operation }, 'auto', '448px');
  }

  addNewOperationInGroup() {
    return this.draftOperationsService.addNewOperationInGroup();
  }

  noHabilAction: OuiBannerAction = {
    label: 'En savoir plus',
    action: () => {
      this.openHabilitationMessage();
    },
  };
  openHabilitationMessage() {
    this.dialogService.openDialog(
      NoneActiveHabilitationMessageComponent,
      {
        title: 'Aucune habilitation active pour cette enveloppe',
      },
      'auto',
      '662px'
    );
  }

  getFirstInvalidOperationId(): string | number | null {
    const ops = this.draftOperationsService.operationGroupData?.operations ?? [];
    const type = this.draftOperationsService.operationGroupData?.operationType;

    // missing nature or product
    if (type === OperationType.ActeDeGestion) {
      const missingNature = ops.find(op => !op.operationConfig?.natureOperation?.id);
      if (missingNature) return missingNature.id ?? null;
    }
    if (type === OperationType.Souscription) {
      const missingProduct = ops.find(op => !op.produit?.id);
      if (missingProduct) return missingProduct.id ?? null;
    }

    // invalid specific form
    const invalidSpecific = ops.find(op => op.specificDataValues?.isFormValid === false);
    //we mark as touched all the specific form to show errors
    if (invalidSpecific?.specificDataValues?.markAsTouched) {
      const comps = this.operationForms.toArray();
      const comp = comps.find(c => c.operation?.id === invalidSpecific.id);
      if (comp) comp.markAsTouched();
    }

    return invalidSpecific?.id ?? null;
  }

  openInvalidAccordionById(): boolean {
    const operationType = this.draftOperationsService.operationGroupData.operationType;
    if (operationType !== OperationType.Souscription && operationType !== OperationType.ActeDeGestion) {
      return false;
    }

    const id = this.getFirstInvalidOperationId();
    if (id == null) return false;

    const comps = this.operationForms.toArray();
    const comp = comps.find(c => c.operation?.id === id);

    if (!comp) return false;

    comp.setErrorBorder(true);
    comp.expanded = true;
    this.cdr?.markForCheck?.();
    return true;
  }
  markAsTouched(): void {
    if (this.enveloppeControl) {
      this.enveloppeControl.markAsTouched();
      this.enveloppeControl.updateValueAndValidity();
    }
    this.operationForms.forEach(opForm => {
      opForm.markAsTouched();
    });
  }
  public touchAllSpecificsInCurrentStep(): void {
    this.operationForms?.forEach(op => op.markSpecificAsTouched());
  }

  public touchSpecificOfIndex(idx: number): void {
    const op = this.operationForms?.toArray()[idx];
    op?.markSpecificAsTouched();
  }
}
